import React, { useState, useEffect, useRef } from "react";
import "../css/css_pages/Dashboard.css";
import Cookies from "js-cookie";
import { FiImage } from "react-icons/fi";
import config from "../../config";
import ChecklistPopup from "./ChecklistPopup";
import { useParams } from "react-router-dom";
import PopupMessage from "../layouts/PopupMessage";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useTranslation } from "react-i18next";

const Schedule = () => {
  const { selectedDate } = useParams();
  const { t } = useTranslation();
  const [token, setToken] = useState(Cookies.get("authToken") || "");
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [comment, setComment] = useState("");
  const [proposedDate, setProposedDate] = useState(new Date());
  const [proposedTime, setProposedTime] = useState("");
  const [alertMessage, setAlertMessage] = useState(null);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [freeDates, setFreeDates] = useState([]);
  const [clientId, setClientId] = useState(null);
  const [date, setDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [latestIssues, setLatestIssues] = useState([]);
  const [latestSchedules, setLatestSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [showAllSchedules, setShowAllSchedules] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(30000);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [pdfSchedule, setPdfSchedule] = useState(null);

  const previousSchedulesRef = useRef([]);
  const pollingRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([fetchSchedules(), fetchLatestData()]);
        // Automatically filter today's schedules after loading
        filterSchedules("today");
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(`${t('failedToFetch')} ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  useEffect(() => {
    if (token) {
      startPolling();
      return () => stopPolling();
    }
  }, [token, pollingInterval]);

  useEffect(() => {
    if (selectedDate) {
      filterSchedulesByDate(selectedDate);
    } else {
      // If no selected date, show today's schedules
      filterSchedules("today");
    }
  }, [selectedDate, schedules, t]);

  useEffect(() => {
    stopPolling();
    startPolling();
  }, [pollingInterval]);

  const fetchSchedules = async () => {
    const token = Cookies.get("authToken");
    console.log("Auth Token:", token);

    if (!token) {
      setError(t("auth_error"));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${config.API_URL}/client/get-all-schedules-by-customer-id`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Schedules API Response:", response);

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        throw new Error(
          errorData.message || `${t('failedToFetch')}: ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("Schedules Data:", data);

      if (Array.isArray(data)) {
        const formattedSchedules = data.map((schedule) => ({
          ...schedule,
          scheduledDate: new Date(schedule.scheduled_date),
          workArrangement: schedule.schedule_mode,
        }));
        setSchedules(formattedSchedules);
        previousSchedulesRef.current = formattedSchedules;
      } else {
        console.log("No schedules found or invalid data format.");
        setSchedules([]);
      }
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setError(`${t('failedToFetch')} ${error.message}`);
    }
  };

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);
    
    // Format as UTC time
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC'  // Use UTC instead of local time
    };

    if (!showOnlyTime) {
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = '2-digit';
    }

    return date.toLocaleString('en-US', options).replace(',', '');
  };


  const fetchLatestData = async () => {
    const token = Cookies.get("authToken");
    if (!token) {
      setError(t("auth_error"));
      return;
    }

    try {
      const [latestReportsRes, latestSchedulesRes] = await Promise.all([
        fetch(`${config.API_URL}/client/get-latest-reported-issues-for-dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${config.API_URL}/client/get-service-schedules-for-dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!latestReportsRes.ok || !latestSchedulesRes.ok) {
        const errorData = await Promise.all([latestReportsRes.json(), latestSchedulesRes.json()]);
        console.error("API Errors:", errorData);
        throw new Error(
          `${t('failedToFetch')}: ${latestReportsRes.statusText} | ${latestSchedulesRes.statusText}`
        );
      }

      const [latestIssues, latestSchedules] = await Promise.all([
        latestReportsRes.json(),
        latestSchedulesRes.json(),
      ]);

      console.log("Issues Data:", latestIssues);
      console.log("Schedules Data:", latestSchedules);

      setLatestIssues(latestIssues);
      setLatestSchedules(latestSchedules);
    } catch (error) {
      console.error("Error fetching latest data:", error);
      setError(`${t('failedToFetch')} ${error.message}`);
    }
  };

  const startPolling = () => {
    const poll = async () => {
      try {
        const previousCount = previousSchedulesRef.current.length;
        await fetchSchedules();

        if (schedules.length === previousCount) {
          setPollingInterval((prev) => Math.min(prev + 30000, 30000));
        } else {
          setPollingInterval(30000);
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };

    poll();
    pollingRef.current = setInterval(poll, pollingInterval);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const fetchFreeDates = async (tsoId) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t("auth_error"));
      }
  
      setFreeDates([]);
  
      const response = await fetch(
        `${config.API_URL}/tso/get-all-free-dates-of-tso/${tsoId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      console.log("API Response:", response);
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("API Error Details:", errorData);
        throw new Error(
          errorData.message || `${t('failedToFetch')}: ${response.statusText}`
        );
      }
  
      const data = await response.json();
      console.log("Raw Free Dates Data:", data);
  
      // Handle case where data might be null or undefined
      if (!data) {
        console.warn("No data received from free dates API");
        setFreeDates([]);
        return;
      }
  
      // Handle case where data is not an array
      if (!Array.isArray(data)) {
        console.warn("Received data is not an array:", data);
        // Try to extract dates if data is an object with dates property
        if (data.dates && Array.isArray(data.dates)) {
          data = data.dates;
        } else {
          setFreeDates([]);
          return;
        }
      }
  
      console.log("Processing Free Dates:", data);
  
      const formattedDates = data.map((item) => {
        // Handle both object with date property and direct date strings
        const dateValue = item.date || item;
        const utcDate = new Date(dateValue);
        
        // Validate the date
        if (isNaN(utcDate.getTime())) {
          console.warn("Invalid date found:", dateValue);
          return null;
        }
  
        const year = utcDate.getFullYear();
        const month = String(utcDate.getMonth() + 1).padStart(2, "0");
        const day = String(utcDate.getDate()).padStart(2, "0");
  
        return `${year}-${month}-${day}`;
      }).filter(date => date !== null); // Filter out any invalid dates
  
      console.log("Formatted Dates:", formattedDates);
      setFreeDates(formattedDates);
    } catch (error) {
      console.error("Error fetching free dates:", error);
      setFreeDates([]);
      showPopupMessage(`${t('failedToFetchFreeDates')}: ${error.message}`);
    }
  };

  const isDateDisabled = ({ date }) => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    if (date < currentDate) {
      return true;
    }

    const dateString = date.toLocaleDateString("en-CA");
    return !freeDates.includes(dateString);
  };

  const showPopupMessage = (message) => {
    setAlertMessage(message);
  };

  const closePopupMessage = () => {
    setAlertMessage(null);
  };


  const filterSchedules = (filterType = "today") => {
    const currentDate = new Date();
    let startDate, endDate;

    switch (filterType) {
      case "selectedDate":
        startDate = endDate = new Date(selectedDate)
          .toLocaleDateString("en-CA")
          .split("T")[0];
        break;
      case "today":
        startDate = endDate = currentDate.toISOString().split("T")[0];
        break;
      case "thisWeek":
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = startOfWeek.toISOString().split("T")[0];
        endDate = endOfWeek.toISOString().split("T")[0];
        break;
      case "thisMonth":
        const startOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        const endOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
        startDate = startOfMonth.toISOString().split("T")[0];
        endDate = endOfMonth.toISOString().split("T")[0];
        break;
      case "thisYear":
        const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
        const endOfYear = new Date(currentDate.getFullYear(), 11, 31);
        startDate = startOfYear.toISOString().split("T")[0];
        endDate = endOfYear.toISOString().split("T")[0];
        break;
      default:
        startDate = endDate = currentDate.toISOString().split("T")[0];
    }

    if (!startDate || !endDate) {
      console.error("Invalid date range.");
      showPopupMessage(t("invalid_date_range"));
      return;
    }

    // Filter schedules based on the date range
    const formattedSchedules = schedules.filter((schedule) => {
      const scheduleDate = new Date(schedule.scheduledDate);
      const scheduleDateString = scheduleDate.toISOString().split("T")[0];
      return scheduleDateString >= startDate && scheduleDateString <= endDate;
    });

    setFilteredSchedules(formattedSchedules);
  };

  const handleCalendarChange = (date) => {
    setProposedDate(date);
    setShowCalendarPopup(false);
    const selectedDate = date.toLocaleDateString("en-CA");
    filterSchedulesByDate(selectedDate);
  };

  const filterSchedulesByDate = (selectedDate) => {
    try {
      let dateToFilter;

      if (selectedDate instanceof Date) {
        dateToFilter = selectedDate.toLocaleDateString("en-CA");
      } else {
        dateToFilter = new Date(selectedDate).toLocaleDateString("en-CA");
      }

      const formattedSchedules = schedules.filter((schedule) => {
        const scheduleDate = new Date(schedule.scheduledDate);
        const scheduleDateString = scheduleDate.toLocaleDateString("en-CA");
        return scheduleDateString === dateToFilter;
      });

      setFilteredSchedules(formattedSchedules);
    } catch (error) {
      console.error("Error filtering schedules by date:", error);
      filterSchedules("today");
    }
  };

  const openResource = async (resource, issue) => {
    setSelectedSchedule(issue);

    const url = `${config.API_URL}/client/view-schedule-image?schedule_id=${issue.schedule_id}&file_name=${resource}`;
    const token = Cookies.get("authToken");
    if (!token) {
      throw new Error(t("auth_error"));
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const fileType = blob.type;

        if (fileType.startsWith("image/")) {
          const imageURL = URL.createObjectURL(blob);
          setImageSrc(imageURL);
        } else {
          const fileURL = URL.createObjectURL(blob);
          window.open(fileURL, "_blank");
        }
      } else {
        console.error("Failed to fetch the resource:", response.statusText);
        showPopupMessage(t("resource_fetch_error"));
      }
    } catch (error) {
      console.error("Error fetching the resource:", error);
      showPopupMessage(t("resource_fetch_error"));
    }
  };

  const closeImageModal = () => {
    setImageSrc(null);
    setSelectedSchedule(null);
  };

  const handleCheck = (issue) => {
    setSelectedSchedule(issue);
    setShowChecklist(true);
  };

  const closeChecklistPopup = () => {
    setShowChecklist(false);
    setSelectedSchedule(null);
  };

  const handleAccept = async (schedule_id) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t("auth_error"));
      }

      const response = await fetch(`${config.API_URL}/client/update-schedule`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          schedule_id: schedule_id,
          action: "accept",
        }),
      });

      if (!response.ok) {
        throw new Error(`${t('failedToAccept')}: ${response.statusText}`);
      }

      const data = await response.json();
      showPopupMessage(t("scheduleAccepted"));
      
      // Force refresh schedules immediately
      await fetchSchedules();
      filterSchedules("today");
    } catch (error) {
      console.error("Error accepting schedule:", error);
      showPopupMessage(t("failedToAccept"));
    }
  };

  const handleReject = async (schedule_id) => {
    if (!comment) {
      showPopupMessage(t("description_required"));
      return;
    }
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t("auth_error"));
      }

      const response = await fetch(`${config.API_URL}/client/update-schedule`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          schedule_id: schedule_id,
          action: "reject",
          comment,
        }),
      });

      if (!response.ok) {
        throw new Error(`${t('failedToReject')}: ${response.statusText}`);
      }

      const data = await response.json();
      showPopupMessage(t("scheduleRejected"));
      setShowRejectModal(false);
      
      // Force refresh schedules immediately
      await fetchSchedules();
      filterSchedules("today");
    } catch (error) {
      console.error("Error rejecting schedule:", error);
      showPopupMessage(t("failedToReject"));
    }
  };

  const handlePropose = async (schedule_id) => {
    if (!proposedDate || !proposedTime) {
      return;
    }

    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t("auth_error"));
      }
      const formattedDate = formatDTime(proposedDate, proposedTime);
      console.log(formattedDate);
      const response = await fetch(`${config.API_URL}/client/update-schedule`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          schedule_id: schedule_id,
          action: "propose",
          proposed_date: formattedDate,
          comment,
        }),
      });

      if (!response.ok) {
        throw new Error(`${t('failedToPropose')}: ${response.statusText}`);
      }

      const data = await response.json();
      showPopupMessage(t("scheduleProposed"));
      setShowProposeModal(false);
      
      // Force refresh schedules immediately
      await fetchSchedules();
      filterSchedules("today");
    } catch (error) {
      console.error("Error proposing schedule:", error);
      showPopupMessage(t("failedToPropose"));
    }
  };

  const viewPDF = async (schedule_id, schedule_no, language) => {
    if (!schedule_id) {
      showPopupMessage(t("no_issues_found"));
      return;
    }

    const pdfWindow = window.open("", "_blank");

    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error(t("auth_error"));
      }

      const response = await fetch(`${config.API_URL}/client/generate-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          schedule_id: schedule_id,
          language: language
        }),
      });

      if (!response.ok) {
        throw new Error(`${t('failedToViewPDF')}: ${response.statusText}`);
      }

      const pdfBlob = await response.blob();
      const pdfUrl = URL.createObjectURL(pdfBlob);

      if (pdfWindow) {
        pdfWindow.location.href = pdfUrl;
      } else {
        showPopupMessage(t("popupBlocked"));
      }
    } catch (error) {
      console.error("Error viewing pdf:", error);
      showPopupMessage(t("failedToViewPDF"));
      if (pdfWindow) {
        pdfWindow.close();
      }
    }
  };

  const handleLanguageSelect = (event) => {
    const selectedLang = event.target.value;
    
    // Check if a valid language (not the placeholder) was selected
    if (selectedLang) { 
      setSelectedLanguage(selectedLang);
      setShowLanguageModal(false);
      viewPDF(pdfSchedule.schedule_id, pdfSchedule.schedule_no, selectedLang);
    }
  };

  const formatDTime = (date, time) => {
    if (!date || !time) return "";
    const dateObj = new Date(date);

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const [hours, minutes] = time.split(":");

    return `${year}-${month}-${day} ${hours}:${minutes}:00`;
  };

  const getDotColor = (status) => {
    switch (status) {
      case "pending":
        return "#faafaf";
      case "confirmed":
        return "#cce5ff";
      case "in_progress":
        return "#fff3cd";
      case "completed":
        return "#E2F7D4";
      case "closed":
        return "#F0D9E7";
      case "proposed":
        return "#EFEFFF";
      default:
        return "#D3D3D3";
    }
  };

  const showPopupCalender = () => {
    setShowCalendarPopup(!showCalendarPopup);
  };

  const tileContent = ({ date, view }) => {
    const currentDate = new Date();
    if (
      date.getFullYear() === currentDate.getFullYear() &&
      date.getMonth() === currentDate.getMonth() &&
      date.getDate() === currentDate.getDate()
    ) {
      return <div className="current-date">{t("today")}</div>;
    }
    return null;
  };

  if (loading) {
    return <div>{t("loadingSchedules")}</div>;
  }

  if (error) {
    return <div className="error-message">{t("errorMessage", { message: error })}</div>;
  }

  return (
    <div className="dashboard-container">
      <h1>{t("schedules")}</h1>

      <div className="filter-buttons">
        <button onClick={() => filterSchedules("today")}>{t("today")}</button>
        <button onClick={() => filterSchedules("thisWeek")}>{t("thisWeek")}</button>
        <button onClick={() => filterSchedules("thisMonth")}>{t("thisMonth")}</button>
        <button onClick={() => filterSchedules("thisYear")}>{t("thisYear")}</button>
      </div>

      {filteredSchedules.length > 0 ? (
        <>
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>{t("id")}</th>
                <th>{t("description")}</th>
                <th>{t("resources")}</th>
                <th>{t("date_time")}</th>
                <th>{t("status")}</th>
                <th>{t("workArrangement")}</th>
                {filteredSchedules.some(
                  (schedule) => schedule.schedule_status === "completed"
                ) && <th>{t("confirmation")}</th>}
                <th>{t("schedule")}</th>
              </tr>
            </thead>
            <tbody>
              {[...filteredSchedules]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((schedule) => (
                  <tr key={schedule.schedule_id}>
                    <td>{schedule.schedule_no}</td>
                    <td style={{ maxWidth: "300px", wordWrap: "break-word" }}>
                      {schedule.schedule_description}
                    </td>
                    <td>
                      {schedule.img_file ? (
                        <>
                          {schedule.img_file
                            .split(",")
                            .map((resource, index) => (
                              <button
                                key={index}
                                className="resource-link"
                                onClick={() => openResource(resource, schedule)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  marginRight: "5px",
                                }}
                              >
                                <FiImage size={20} color="#1C3663" />
                              </button>
                            ))}
                        </>
                      ) : (
                        t("no_resources")
                      )}
                    </td>
                    <td>
                      <input
                        value={formatDateTime(schedule.scheduled_date)}
                        readOnly
                        style={{
                          border: "none",
                          backgroundColor: "#cce5ff",
                          color: "black",
                          fontFamily: "inherit",
                          fontSize: "inherit",
                          width: "75%",
                          cursor: "default",
                          borderRadius: "100px",
                        }}
                      />
                    </td>
                    <td
                      style={{
                        backgroundColor: getDotColor(schedule.schedule_status),
                        color:
                          schedule.schedule_status === "pending"
                            ? "#2f0909"
                            : schedule.schedule_status === "in_progress"
                            ? "#856404"
                            : schedule.schedule_status === "completed"
                            ? "#155724"
                            : schedule.schedule_status === "closed"
                            ? "#1b1e21"
                            : "#212529",
                        padding: "5px",
                        textAlign: "center",
                      }}
                    >
                      {t(schedule.schedule_status.replace(/_/g, " ").toLowerCase())}
                    </td>
                    <td>
                      <div
                        style={{
                          backgroundColor: schedule.workArrangement === "remote" ? "#E8D5FF" : "#D5FBC8",
                          color: "black",
                          padding: "5px",
                          borderRadius: "4px",
                          textAlign: "center",
                          textTransform: "uppercase",
                        }}
                      >
                        {t(schedule.workArrangement)}
                      </div>
                    </td>
                    {filteredSchedules.some(
                      (s) => s.schedule_status === "completed"
                    ) && (
                      <td>
                        {(schedule.schedule_status === "completed" ||
                          schedule.schedule_status === "closed") && (
                          <button
                            onClick={() => {
                              handleCheck(schedule);
                            }}
                            style={{
                              backgroundColor:
                                schedule.schedule_status === "closed"
                                  ? "#cccccc"
                                  : "#3f704d",
                              color: "white",
                              borderRadius: "5px",
                              border: "none",
                              cursor: "pointer",
                              marginRight: "5px",
                              padding: "5px 10px",
                            }}
                          >
                            {schedule.schedule_status === "closed"
                              ? t("view")
                              : t("confirm")}
                          </button>
                        )}
                      </td>
                    )}
                    <td>
                      {schedule.schedule_status === "pending" ||
                      schedule.schedule_status === "proposed" ? (
                        <>
                          <button
                            onClick={() => {
                              handleAccept(schedule.schedule_id);
                              fetchFreeDates(schedule.assigned_tso_id);
                              showPopupMessage(
                                t("scheduleAccepted")
                              );
                            }}
                            style={{
                              backgroundColor: "#4CAF50",
                              color: "white",
                              borderRadius: "5px",
                              border: "none",
                              cursor: "pointer",
                              marginRight: "5px",
                              padding: "5px 10px",
                            }}
                          >
                            {t("accept")}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedSchedule(schedule);
                              setShowRejectModal(true);
                            }}
                            style={{
                              backgroundColor: "#f44336",
                              color: "white",
                              borderRadius: "5px",
                              border: "none",
                              cursor: "pointer",
                              marginRight: "5px",
                              padding: "5px 10px",
                              marginTop: "5px",
                            }}
                          >
                            {t("reject")}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedSchedule(schedule);
                              setShowProposeModal(true);
                              fetchFreeDates(schedule.assigned_tso_id);
                            }}
                            style={{
                              backgroundColor: "#1C3663",
                              color: "white",
                              borderRadius: "5px",
                              border: "none",
                              cursor: "pointer",
                              padding: "5px 10px",
                              marginTop: "5px",
                            }}
                          >
                            {t("reschedule")}
                          </button>
                        </>
                      ) : schedule.schedule_status === "completed" ||
                        schedule.schedule_status === "closed" ? (
                        <button
                          onClick={() => {
                            setPdfSchedule(schedule);
                            setShowLanguageModal(true);
                          }}
                          style={{
                            backgroundColor: "#4CAF50",
                            color: "white",
                            borderRadius: "5px",
                            border: "none",
                            cursor: "pointer",
                            marginRight: "5px",
                            padding: "5px 10px",
                          }}
                        >
                          {t("viewReport")}
                        </button>
                      ) : (
                        <span
                          style={{ color: "gray", cursor: "not-allowed" }}
                          title="Not available"
                        >
                          {t("noActions")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      ) : (
        <p>{t("no_schedules_found")}</p>
      )}

      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close" onClick={() => setShowRejectModal(false)}>
              &times;
            </span>
            <h2>{t("rejectSchedule")}</h2>
            <label>{t("comment")}</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("comment")}
            />
            <button onClick={() => handleReject(selectedSchedule.schedule_id)}>
              {t("submit")}
            </button>
          </div>
        </div>
      )}

      {showProposeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close" onClick={() => setShowProposeModal(false)}>
              &times;
            </span>

            <h2>
              {t("proposeNewDate")} <br />
              {t("assignedTSOName", { name: selectedSchedule?.assigned_tso_name })}
            </h2>
            <div
              className="form-group"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              <label>{t("proposedDate")}</label>
              <button
                className="date-time-picker"
                style={{ width: 250 }}
                onClick={showPopupCalender}
              >
                {t("selectDate")}
              </button>
            </div>

            <div
              className="form-group"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              <label>{t("proposedTime")}</label>
              <input
                style={{ width: 250 }}
                type="time"
                value={proposedTime}
                onChange={(e) => setProposedTime(e.target.value)}
              />
            </div>
            <label>{t("comment")}</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("comment")}
            />

            <button onClick={() => handlePropose(selectedSchedule.schedule_id)}>
              {t("submit")}
            </button>
          </div>
        </div>
      )}

      {showCalendarPopup && (
        <div
          className="calendar-popup-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="calendar-popup-content"
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
              position: "relative",
            }}
          >
            <span
              style={{
                position: "absolute",
                top: "3px",
                right: "5px",
                cursor: "pointer",
                fontSize: "20px",
                color: "#555",
                fontWeight: "normal",
              }}
              onClick={() => setShowCalendarPopup(false)}
            >
              &times;
            </span>
            <Calendar
              onChange={handleCalendarChange}
              value={proposedDate}
              tileDisabled={isDateDisabled}
              tileContent={tileContent}
            />
          </div>
        </div>
      )}

      {imageSrc && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={closeImageModal}>
              &times;
            </span>
            <h3>{t("fileDetails")}</h3>
            <p>{t("name")}: {selectedSchedule.name}</p>
            <p>{t("type_label")}: {selectedSchedule.type}</p>
            <img src={imageSrc} alt="Selected" style={{ maxWidth: "100%" }} />
          </div>
        </div>
      )}

      {showChecklist && selectedSchedule && (
        <ChecklistPopup
          checklist={selectedSchedule}
          onClose={closeChecklistPopup}
        />
      )}

      {showLanguageModal && pdfSchedule && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Select Language</h2>
            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              
              {/* --- REPLACED BUTTONS WITH DROPDOWN --- */}
              <select 
                onChange={handleLanguageSelect} // Use the new handler
                defaultValue="" // Sets the default to the placeholder
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "1rem",
                  minWidth: "200px", // Keeps a similar width
                }}
              >
                {/* Placeholder option */}
                <option value="" disabled>
                  Select a language...
                </option>
                
                {/* Language options */}
                <option value="si">Sinhala</option>
                <option value="ta">Tamil</option>
              </select>
              {/* --- END OF REPLACEMENT --- */}

            </div>
            <span
              className="close"
              onClick={() => setShowLanguageModal(false)}
              style={{ position: "absolute", top: "10px", right: "15px", cursor: "pointer" }}
            >
              &times;
            </span>
          </div>
        </div>
      )}

      {alertMessage && (
        <PopupMessage message={alertMessage} onClose={closePopupMessage} />
      )}
    </div>
  );
};

export default Schedule;
